import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { CtaBand } from "../components/CtaBand";
import { Seo, faqJsonLd, breadcrumbJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { getCity, REGIONS } from "../data/cities";
import { SERVICES } from "../data/services";
import { cityContent } from "../content";
import { CITY_INTROS } from "../data/cityIntros";
import { MAJOR_CITIES, CORE_SERVICES } from "../seoPolicy";

const SKYLINE = "https://images.unsplash.com/photo-1594825505684-5a5a5b18b544?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function CityPage({ lang }) {
  const { citySlug } = useParams();
  const t = T[lang];
  const p = paths(lang);
  const c = getCity(citySlug);
  const content = cityContent(citySlug, lang);
  if (!c || !content) return <Navigate to={p.cities} replace />;

  const customIntro = CITY_INTROS[citySlug];
  const introParas = customIntro ? customIntro[lang] : content.intro;
  const visibleServices = MAJOR_CITIES.includes(citySlug)
    ? SERVICES
    : SERVICES.filter((s) => CORE_SERVICES.includes(s.slug));

  return (
    <div data-testid={`city-page-${citySlug}`}>
      <Seo lang={lang} title={content.title} description={content.description}
        enPath={`/quebec-locations/${citySlug}`} frPath={`/fr/villes-quebec/${citySlug}`}
        jsonLd={[faqJsonLd(content.faq), breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.breadcrumbCities, p.cities], [c[lang === "fr" ? "fr" : "en"], p.city(citySlug)]])]} />
      <PageHeader lang={lang} eyebrow={`${content.region[lang]} · Québec`} title={content.h1}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.breadcrumbCities, p.cities], [lang === "fr" ? c.fr : c.en, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              {introParas.map((para, i) => (
                <p key={i} className="mb-5 text-base leading-relaxed text-slate-300">{para}</p>
              ))}
            </Reveal>
            <Reveal className="my-8 overflow-hidden rounded-2xl border border-slate-800/70">
              <img src={SKYLINE} alt={lang === "fr" ? `Services de sécurité à ${c.fr}, Québec` : `Security guard services in ${c.en}, Quebec`} className="h-64 w-full object-cover" data-testid="city-image" />
            </Reveal>
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{content.needsH2}</h2>
              {content.needs.map((para, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-slate-400">{para}</p>
              ))}
            </Reveal>
            <Reveal>
              <h2 className="mt-12 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.whyTitle} — {lang === "fr" ? c.fr : c.en}</h2>
              <ul className="mt-6 space-y-3">
                {content.why.map((w) => (
                  <li key={w} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-amber-400" /> {w}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-amber-500/25 bg-card/80 p-6">
                <h2 className="font-display text-lg font-bold">{t.ctaBandTitle}</h2>
                <p className="mt-2 text-sm text-slate-400">{t.ctaBandSub}</p>
                <a href="tel:+18077700243" className="mt-5 block rounded-full bg-amber-500 px-5 py-3 text-center text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="city-aside-call">+1 807-770-0243</a>
                <a href="mailto:admin@1stnation-security.com" className="mt-3 block rounded-full border border-amber-500/50 px-5 py-3 text-center text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10" data-testid="city-aside-email">admin@1stnation-security.com</a>
              </div>
              <div className="rounded-2xl border border-slate-800/70 bg-card/60 p-6">
                <h2 className="flex items-center gap-2 font-display text-base font-bold"><MapPin size={16} className="text-amber-400" /> {t.nearbyTitle} {lang === "fr" ? c.fr : c.en}</h2>
                <ul className="mt-4 space-y-2">
                  {content.nearby.map((n) => (
                    <li key={n.slug}>
                      <Link to={p.city(n.slug)} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid={`nearby-city-${n.slug}`}>
                        {lang === "fr" ? n.fr : n.en} <span className="text-slate-600">· {REGIONS[n.region][lang]}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-slate-800/60 bg-card/30 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{content.servicesH2}</h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 4) * 0.05}>
                  <Link to={p.combo(s.slug, citySlug)} className="group flex h-full items-start gap-3 rounded-xl border border-slate-800/70 bg-obsidian/60 p-5 transition-all hover:-translate-y-0.5 hover:border-amber-500/40" data-testid={`city-service-${s.slug}`}>
                    <Icon size={20} className="mt-0.5 shrink-0 text-amber-400" />
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 group-hover:text-amber-300">
                        {s[lang].name} {lang === "fr" ? "à" : "in"} {lang === "fr" ? c.fr : c.en}
                      </h3>
                      <span className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-amber-400">{t.viewService} <ArrowRight size={12} /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Faq items={content.faq} title={t.faqTitle} />
      <CtaBand lang={lang} />
    </div>
  );
}
